import React, { useContext, useLayoutEffect, useState } from 'react';
import cn from 'classnames';
import ddt from '@wix/da-ddt';
import { PapiDeviation } from '@wix/da-papi-types';
import { Dims } from '@wix/da-hooks/pkg/useMeasureElement/redux/types';
import noop from '../../utils/noop';
import { SubscriptionPurchaseFTUEContext } from '../../Tier/ftue/SubscriptionPurchaseFTUE/SubscriptionPurchaseFTUEContext';
import SubscriptionPurchaseFTUE from '../../Tier/ftue/SubscriptionPurchaseFTUE/SubscriptionPurchaseFTUE';
import { useViewBlockChecks } from '../../DeviationViews/Full/hooks/useViewBlockChecks';
import { MultiArtDisplayMode, DefaultArtStageProps as Props } from '../types';
import { DEFAULT_MULTI_IMAGE_STAGE } from '../constants';
import { useMobileStage } from '../hooks/useMobileStage';
import { useAdditionalMedia } from '../hooks/useAdditionalMedia';
import { useMultiArtCurrentImageTracking } from '../hooks/useMultiArtCurrentImageTracking';
import { useMultiArtDeepLinking } from '../hooks/useMultiArtDeepLinking';
import Duperswiper from '../_partials/Duperswiper';
import ZoomModalFromImageSource from '../_partials/ZoomModalFromImageSource';
import MobileMultiArtScrollStage from './MobileMultiArtScrollStage';
import MobileMultiArtCarouselStage from './MobileMultiArtCarouselStage';

import styles from './MobileArtStage.scss';

const logger = ddt.createLogger('multifile');

export const MobileMultiArtStage: React.FC<Props> = ({
  deviation,
  className,
  zoomedImageSource,
  isZoomed,
  onEnterZoomMode = noop,
  onExitZoomMode = noop,
  ...props
}) => {
  const [displayMode, setDisplayMode] = useState<MultiArtDisplayMode>(
    DEFAULT_MULTI_IMAGE_STAGE
  );
  // if blocked/disabled, only show carousel
  const { hasBlock, isBlockedByMature } = useViewBlockChecks(deviation);
  const StageComponent =
    displayMode === 'scroll' && !hasBlock
      ? MobileMultiArtScrollStage
      : MobileMultiArtCarouselStage;

  const additionalMedia = useAdditionalMedia(deviation);
  const [currentlyViewedImageSource, setCurrentlyViewedImageSource] =
    useMultiArtCurrentImageTracking(deviation, additionalMedia);
  useMultiArtDeepLinking(additionalMedia, setCurrentlyViewedImageSource);

  // when duperbrowse switches between deviations, reset to show main image
  useLayoutEffect(() => {
    setCurrentlyViewedImageSource(deviation);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [deviation.deviationId]);

  const isSubscriptionPurchaseFTUEBeingShown = !!useContext(
    SubscriptionPurchaseFTUEContext
  );

  const { containerSize = {} as Dims } = useMobileStage(deviation);

  logger.log('DesktopMultiArtStage', {
    deviation: deviation.deviationId,
    displayMode,
    hasBlock,
    currentlyViewedImageSource:
      currentlyViewedImageSource?.['title'] ??
      currentlyViewedImageSource?.['filename'],
    isSubscriptionPurchaseFTUEBeingShown,
    containerSize,
  });

  return (
    <div
      className={cn(styles['root'], styles[`${displayMode}-mode`], className)}
    >
      <Duperswiper
        className={styles['dont-hide-overflow-on-swiper']}
        deviation={deviation}
        width={containerSize?.width}
        height="auto"
        isInZoomModal={isZoomed}
      >
        {duperswiperDeviation => {
          return (
            <StageComponent
              {...props}
              deviation={duperswiperDeviation}
              additionalMedia={additionalMedia}
              disabled={hasBlock}
              // blurring for mature is clientside
              shouldBlur={isBlockedByMature}
              setDisplayMode={setDisplayMode}
              currentlyViewedImageSource={currentlyViewedImageSource}
              setCurrentlyViewedImageSource={setCurrentlyViewedImageSource}
              isZoomed={isZoomed}
              onEnterZoomMode={onEnterZoomMode}
              onExitZoomMode={onExitZoomMode}
            />
          );
        }}
      </Duperswiper>

      {zoomedImageSource && (
        <ZoomModalFromImageSource
          source={zoomedImageSource}
          allowFullviewSrcsetForPngs={
            (zoomedImageSource as PapiDeviation)?.isPublished ?? false
          }
          {...containerSize}
          isZoomed={isZoomed}
          onExitZoomMode={onExitZoomMode}
        />
      )}

      {isSubscriptionPurchaseFTUEBeingShown && (
        <SubscriptionPurchaseFTUE
          className={styles['subscription-purchase-ftue']}
        />
      )}
    </div>
  );
};
MobileMultiArtStage.displayName = 'MobileMultiArtStage';
export default MobileMultiArtStage;
